<template>
  <b-sidebar
    id="sidebar_user_assigned_regions"
    :visible="isSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    no-close-on-backdrop
    right
    @shown="loadData"
    @hidden="resetForm"
    @change="(val) => $emit('update:is-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0 text-capitalize">
          {{
            isAdd
              ? $t("assigned_regions.schedule_regions")
              : $t("actions.save")
          }}
        </h5>

        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />
      </div>

      <validation-observer
        #default="{ handleSubmit }"
        ref="refFormObserver"
      >
        <b-form
          class="p-2"
          @submit.prevent="handleSubmit(onSubmit)"
          @reset.prevent="resetForm"
        >
          <!-- Supervisor -->
          <b-form-group
            class="text-capitalize"
            :label="$t('assigned_regions.supervisor')"
          >
            <b-form-input
              :value="currentSupervisorLabel"
              disabled
            />

            <small class="text-muted">
              {{ $t("assigned_regions.current_supervisor") }}
            </small>
          </b-form-group>

          <!-- Analyst -->
          <validation-provider
            :name="$t('assigned_regions.analyst')"
            rules="required"
            v-slot="{ errors }"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('assigned_regions.analyst')"
            >
              <select-paginate
                v-model="analyst"
                :items="analystOptions"
                :class="{ 'is-invalid': errors.length }"
              />

              <b-form-invalid-feedback>
                {{ errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Sectors -->
          <validation-provider
            :name="$t('assigned_regions.sectors')"
            rules="required"
            v-slot="{ errors }"
          >
            <b-form-group class="text-capitalize">
              <div
                class="d-flex align-items-center justify-content-between mb-50"
              >
                <label class="mb-0">
                  {{ $t("assigned_regions.sectors") }}
                </label>

                <b-form-checkbox
                  :checked="allSectorsSelected"
                  :indeterminate="someSectorsSelected"
                  :disabled="!sectorOptions.length"
                  class="mb-0"
                  @change="toggleAllSectors"
                >
                  Seleccionar todos
                </b-form-checkbox>
              </div>

              <v-select
                v-model="sectorIds"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="sectorOptions"
                :reduce="getSectorOptionValue"
                :close-on-select="false"
                multiple
                append-to-body
                :calculate-position="withPopper"
                :class="{ 'is-invalid': errors.length }"
              >
                <template #no-options>
                  No se encontraron sectores
                </template>
              </v-select>

              <b-form-invalid-feedback class="d-block">
                {{ errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Init date -->
          <validation-provider
            :name="$t('assigned_regions.init_date')"
            rules="required"
            v-slot="{ errors }"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('assigned_regions.init_date')"
            >
              <b-form-input
                v-model="itemData.init_date"
                type="datetime-local"
                :class="{ 'is-invalid': errors.length }"
              />

              <b-form-invalid-feedback>
                {{ errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- End date -->
          <validation-provider
            :name="$t('assigned_regions.end_date')"
            rules="required"
            v-slot="{ errors }"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('assigned_regions.end_date')"
            >
              <b-form-input
                v-model="itemData.end_date"
                type="datetime-local"
                :class="{ 'is-invalid': errors.length }"
              />

              <b-form-invalid-feedback>
                {{ errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2 text-capitalize"
              type="submit"
              :disabled="isSaving"
            >
              <b-spinner
                v-if="isSaving"
                small
                class="mr-50"
              />

              {{ isAdd ? $t("actions.add") : $t("actions.save") }}
            </b-button>

            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              class="text-capitalize"
              :disabled="isSaving"
              @click="hide"
            >
              {{ $t("actions.cancel") }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BFormCheckbox,
  BSpinner,
} from "bootstrap-vue";

import {
  ValidationProvider,
  ValidationObserver,
} from "vee-validate";

import {
  ref,
  computed,
} from "@vue/composition-api";

import { required } from "@validations";
import { useToast } from "vue-toastification/composition";

import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import formValidation from "@core/comp-functions/forms/form-validation";

import Ripple from "vue-ripple-directive";
import store from "@/store";
import axios from "@/libs/axios";
import moment from "moment-timezone";
import vSelect from "vue-select";
import popperHelper from "@/libs/popper-helper";

import { decryptData } from "@/utils/decryptUserDataUtils";

import SelectPaginate from "../components/SelectPaginate.vue";

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    BFormCheckbox,
    BSpinner,
    ValidationProvider,
    ValidationObserver,
    SelectPaginate,
    vSelect,
  },

  directives: {
    Ripple,
  },

  model: {
    prop: "isSidebarActive",
    event: "update:is-sidebar-active",
  },

  props: {
    isSidebarActive: {
      type: Boolean,
      required: true,
    },

    itemEdit: {
      type: Object,
      default: () => ({}),
    },

    isAdd: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      required,
    };
  },

  setup(props, { emit }) {
    const toast = useToast();

    const isSaving = ref(false);
    const analystOptions = ref([]);

    const analyst = ref(null);
    const sectorIds = ref([]);

    const currentUser = JSON.parse(
      decryptData(window.localStorage.userData)
    );

    const blankData = {
      id: null,
      init_date: null,
      end_date: null,
    };

    const itemData = ref(
      JSON.parse(JSON.stringify(blankData))
    );

    const sectorOptions = computed(() => {
      return (
        store.getters["sector/getSelectedItems"] ||
        store.getters["sector/getItems"] ||
        []
      );
    });

    const getSectorOptionValue = option => {
      if (!option) {
        return null;
      }

      return option.value ?? option.id ?? null;
    };

    const allSectorIds = computed(() => {
      const ids = sectorOptions.value
        .map(getSectorOptionValue)
        .filter(
          id =>
            id !== null &&
            id !== undefined
        );

      return [...new Set(ids)];
    });

    const allSectorsSelected = computed(() => {
      if (!allSectorIds.value.length) {
        return false;
      }

      return allSectorIds.value.every(sectorId =>
        sectorIds.value.some(
          selectedId =>
            Number(selectedId) === Number(sectorId)
        )
      );
    });

    const someSectorsSelected = computed(() => {
      if (!allSectorIds.value.length) {
        return false;
      }

      const selectedCount = allSectorIds.value.filter(
        sectorId =>
          sectorIds.value.some(
            selectedId =>
              Number(selectedId) === Number(sectorId)
          )
      ).length;

      return (
        selectedCount > 0 &&
        selectedCount < allSectorIds.value.length
      );
    });

    const toggleAllSectors = checked => {
      if (checked) {
        sectorIds.value = [...allSectorIds.value];
        return;
      }

      sectorIds.value = [];
    };

    const buildUserOptionLabel = user => {
      const parts = [
        user?.name,
        user?.user,
        user?.email,
      ].filter(Boolean);

      return parts.join(" | ");
    };

    const currentSupervisorLabel = computed(() => {
      return (
        buildUserOptionLabel(currentUser) ||
        `#${currentUser.id}`
      );
    });

    const showErrorToast = message => {
      toast({
        component: ToastificationContent,
        props: {
          title: message,
          icon: "AlertTriangleIcon",
          variant: "danger",
        },
      });
    };

    const showSuccessToast = message => {
      toast({
        component: ToastificationContent,
        props: {
          title: message,
          icon: "CheckIcon",
          variant: "success",
        },
      });
    };

    const loadSectorOptions = async () => {
      try {
        await store.dispatch("sector/getAll", {
          per_page: 500,
          max: 500,
        });
      } catch (error) {
        console.error(
          "Error cargando sectores:",
          error
        );

        showErrorToast(
          "No fue posible cargar los sectores"
        );

        throw error;
      }
    };

    const loadAnalystOptions = async () => {
      try {
        const { data } = await axios.get(
          "v1/user_assigned_regions/getAnalysts"
        );

        const list = data?.data || [];

        analystOptions.value = list.map(user => ({
          value: user.id,
          label:
            buildUserOptionLabel(user) ||
            `#${user.id}`,
        }));
      } catch (error) {
        analystOptions.value = [];

        console.error(
          "Error cargando analistas:",
          error
        );

        showErrorToast(
          "No fue posible cargar los analistas"
        );

        throw error;
      }
    };

    const resetData = () => {
      itemData.value = JSON.parse(
        JSON.stringify(blankData)
      );

      analyst.value = null;
      sectorIds.value = [];
    };

    const toInputDatetimeLocal = value => {
      if (!value) {
        return null;
      }

      return moment(value).format(
        "YYYY-MM-DDTHH:mm"
      );
    };

    const findAnalystOption = userLike => {
      if (!userLike) {
        return null;
      }

      const id =
        userLike.id ||
        userLike.analyst_user_id ||
        userLike.value;

      const match = analystOptions.value.find(
        option =>
          Number(option.value) === Number(id)
      );

      if (match) {
        return match;
      }

      return id
        ? {
            value: id,
            label:
              buildUserOptionLabel(userLike) ||
              `#${id}`,
          }
        : null;
    };

    const loadData = async () => {
      try {
        await Promise.all([
          loadSectorOptions(),
          loadAnalystOptions(),
        ]);

        if (
          !props.isAdd &&
          props.itemEdit &&
          props.itemEdit.id
        ) {
          const row = props.itemEdit;

          itemData.value.id = row.id;

          itemData.value.init_date =
            toInputDatetimeLocal(row.init_date);

          itemData.value.end_date =
            toInputDatetimeLocal(row.end_date);

          analyst.value = row.analyst
            ? findAnalystOption(row.analyst)
            : row.analyst_user_id
              ? findAnalystOption({
                  id: row.analyst_user_id,
                })
              : null;

          sectorIds.value = Array.isArray(row.sectors)
            ? row.sectors
                .map(sector => sector.id)
                .filter(
                  id =>
                    id !== null &&
                    id !== undefined
                )
            : [];

          return;
        }

        resetData();
      } catch (error) {
        console.error(
          "Error cargando datos del sidebar:",
          error
        );
      }
    };

    const onSubmit = () => {
      if (!analyst.value) {
        showErrorToast(
          "Selecciona un analista"
        );

        return;
      }

      if (!sectorIds.value.length) {
        showErrorToast(
          "Selecciona al menos un sector"
        );

        return;
      }

      const analystUserId =
        typeof analyst.value === "object"
          ? analyst.value.value
          : analyst.value;

      const payload = {
        analyst_user_id: analystUserId,
        supervisor_user_id: currentUser.id,
        sector_ids: sectorIds.value,
        init_date: itemData.value.init_date,
        end_date: itemData.value.end_date,
        scheduled: 1,
      };

      isSaving.value = true;

      const dispatch = props.isAdd
        ? store.dispatch(
            "user_assigned_region/add",
            payload
          )
        : store.dispatch(
            "user_assigned_region/edit",
            {
              id: itemData.value.id,
              params: payload,
            }
          );

      dispatch
        .then(response => {
          showSuccessToast(
            response?.message ||
              (props.isAdd
                ? "Registro programado creado"
                : "Registro programado actualizado")
          );

          emit("refetch-data");

          emit(
            "update:is-sidebar-active",
            false
          );
        })
        .catch(error => {
          const message =
            error?.response?.data?.message ||
            "Error guardando la programación";

          showErrorToast(message);
        })
        .finally(() => {
          isSaving.value = false;
        });
    };

    const {
      refFormObserver,
      getValidationState,
      resetForm,
    } = formValidation(resetData);

    return {
      withPopper: popperHelper.withPopper,

      analystOptions,
      sectorOptions,
      currentSupervisorLabel,

      analyst,
      sectorIds,
      itemData,

      getSectorOptionValue,
      allSectorsSelected,
      someSectorsSelected,
      toggleAllSectors,

      onSubmit,
      loadData,

      refFormObserver,
      getValidationState,
      resetForm,

      isSaving,
    };
  },
};
</script>
